import { SessionAuthAPI } from "./SessionAuthAPIInterface";
import { SessionData } from "../../domain/Session";
import { ConsentConfig } from "../../domain/Consent";
import { AuthRequest } from "../../domain/AuthRequest";
import { errorConsentNotFound, errorInvalidCredentials, errorUserNotFound } from "./Errors";


async function sendRequest(httpMethod: string, url: string, body?: any): Promise<Response> {
    const req: RequestInit = {
        method: httpMethod,
        mode: 'cors',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
    };
    if (body) {
        req.body = JSON.stringify(body);
    }
    return await fetch(url, req);
}

const customDomain = process.env.REACT_APP_API_URL || '';

export const authAPI: SessionAuthAPI = {
    async submitUsername(loginName: string, authRequestID: string): Promise<SessionData> {
        const rsp = await sendRequest('POST', `${customDomain}/v1/sessions`, {
            "checks": {
                "user": {
                    "loginName": loginName
                }
            },
            "metadata": {
                "authRequestID": authRequestID
            }
        }
        );
        if (rsp.status === 404) {
            throw errorUserNotFound
        }
        if (!rsp.ok) {
            throw new Error('Error submitting username');
        }
        
        return await rsp.json() as SessionData;
    },

    async submitPassword(password: string, sessionId: string): Promise<SessionData> {
        const rsp = await sendRequest('PATCH', `${customDomain}/v1/sessions/${sessionId}`, {
            "checks": {
                "password": {
                    "password": password
                }
            }
        }
        );
        if (rsp.status === 400) {
            throw errorInvalidCredentials
        }
        if (!rsp.ok) {
            throw new Error('Error submitting password');
        }
        return await rsp.json() as SessionData;
    },

    async submitOTP(code: string, sessionId: string): Promise<SessionData> {
        const rsp = await sendRequest('PATCH', `${customDomain}/v1/sessions/${sessionId}`, {
            "checks": {
                "totp": {
                    "code": code
                }
            }
        }
        );
        if (rsp.status === 400) {
            throw errorInvalidCredentials
        }
        if (!rsp.ok) {
            throw new Error('Error submitting totp');
        }
        
        return await rsp.json() as SessionData;
    },

    async submitOtpEmail(code:string, sessionId: string): Promise<SessionData> {
        const rsp = await sendRequest('PATCH', `${customDomain}/v1/sessions/${sessionId}`, {
            "checks": {
                "otpEmail": {
                    "code": code
                }
            }
        }
        );
        if (rsp.status === 400) {
            throw errorInvalidCredentials
        }
        if (!rsp.ok) {
            throw new Error('Error submitting opt email');
        }

        return await rsp.json() as SessionData;
    },

    async submitOtpSMS(code: string, sessionId: string): Promise<SessionData> {
        const rsp = await sendRequest('PATCH', `${customDomain}/v1/sessions/${sessionId}`, {
            "checks": {
                "otpSms": {
                    "code": code
                }
            }
        }
        );
        if (rsp.status === 400) {
            throw errorInvalidCredentials
        }
        if (!rsp.ok) {
            throw new Error('Error submitting opt sms');
        }
        
        return await rsp.json() as SessionData;
    },

    async sendOtpEmail(sessionId: string): Promise<SessionData> {
        const rsp = await sendRequest('PATCH', `${customDomain}/v1/sessions/${sessionId}`, {
            "challenges": {
                "otpEmail": {
                }
            }
        }
        );
        if (!rsp.ok) {
            throw new Error('Error submitting opt sms');
        }
        
        return await rsp.json() as SessionData;
    },

    async sendOtpSms(sessionId: string): Promise<SessionData> {
        const rsp = await sendRequest('PATCH', `${customDomain}/v1/sessions/${sessionId}`, {
            "challenges": {
                "otpSms": {
                    "returnCode": false
                }
            }
        }
        );
        if (!rsp.ok) {
            throw new Error('Error submitting opt sms');
        }
        
        return await rsp.json() as SessionData;
    },

    async getConsentConfig(): Promise<ConsentConfig> {
        const rsp = await sendRequest('GET', `${customDomain}/v1/sessions/consent/config`);
        if (rsp.status === 404) {
            throw errorConsentNotFound
        }
        if (!rsp.ok) {
            throw new Error('Error fetching consent configuration');
        }
        
        return await rsp.json() as ConsentConfig;
    },

    async submitConsent(consentData: Map<string,string[]>): Promise<void> {
        const rsp = await sendRequest('POST', `${customDomain}/v1/sessions/consent`, {
            "granted_resources": Object.fromEntries(consentData)
          });
        if (!rsp.ok) {
            throw new Error('Error submitting consent');
        }
        return;
    },

    async sendAuthRequest(): Promise<AuthRequest> {
        const rsp = await sendRequest('POST', `${customDomain}/v1/sessions/auth_request`);
        if (!rsp.ok) {
            throw new Error('Error sending auth request');
        }
        return await rsp.json() as AuthRequest;
    }

};
